import { useCallback, useEffect, useRef } from 'react';
import maplibregl from 'maplibre-gl';
import type { Feature, LineString } from 'geojson';
import 'maplibre-gl/dist/maplibre-gl.css';
import { config } from '../api/config';
import type { LatLon, NearbyDriver, RoutePoint } from '../domain/types';
import { decodePolyline } from './polyline';
import { DriverTracker, bearingDeg } from './driverTracker';
import osmLibertyStyle from './styles/osm-liberty.json';

type PointSlot = RoutePoint | null;

type MapViewProps = {
  center?: LatLon | null;
  points?: PointSlot[];
  routeGeometry?: string | null;
  // Assigned driver: latest raw position (from SignalR / REST fallback). The
  // car marker is animated along `driverRouteGeometry` between updates.
  driver?: LatLon | null;
  driverRouteGeometry?: string | null;
  followDriver?: boolean;
  // Free cars around the pickup (ether), shown while building/searching.
  nearbyDrivers?: NearbyDriver[];
};

const ROUTE_SOURCE = 'route-src';
const ROUTE_LAYER = 'route-line';
const PREVIEW_SOURCE = 'route-preview-src';
const PREVIEW_LAYER = 'route-preview-line';
const DRIVER_ROUTE_SOURCE = 'driver-route-src';
const DRIVER_ROUTE_LAYER = 'driver-route-line';

// Top-down car icon pointing north (up). Rotated clockwise by the tracker's
// bearing via Marker.setRotation with rotationAlignment 'map'.
const CAR_SVG =
  '<svg width="34" height="40" viewBox="0 0 34 40" xmlns="http://www.w3.org/2000/svg">' +
  '<ellipse cx="17" cy="37" rx="10" ry="2.5" fill="rgba(0,0,0,0.28)"/>' +
  '<rect x="8" y="4" width="18" height="32" rx="7" fill="#13202b" stroke="#ffffff" stroke-width="1.5"/>' +
  '<rect x="11" y="7" width="12" height="7" rx="2.5" fill="#8fd3ff"/>' +
  '<rect x="11" y="17" width="12" height="6" rx="1.5" fill="#cfe9ff" opacity="0.45"/>' +
  '<rect x="11" y="26" width="12" height="5" rx="2" fill="#5b6b7a"/>' +
  '</svg>';

// Smaller, lighter car for nearby free drivers (ether) — visually distinct from
// the assigned driver. Rotated by bearing from consecutive positions.
const NEARBY_CAR_SVG =
  '<svg width="24" height="28" viewBox="0 0 24 28" xmlns="http://www.w3.org/2000/svg">' +
  '<rect x="6" y="3" width="12" height="22" rx="5" fill="#3b4a57" stroke="#cfd8e0" stroke-width="1"/>' +
  '<rect x="8" y="5.5" width="8" height="5" rx="1.5" fill="#bfe3ff"/>' +
  '<rect x="8" y="18.5" width="8" height="3.5" rx="1" fill="#7d8b97"/>' +
  '</svg>';

// Sentinel values selectable via VITE_MAP_STYLE_URL.
const OSM_RASTER = 'osm-raster';
const OSM_LIBERTY = 'osm-liberty';
// Placeholder key shipped in the upstream osm-liberty style; replaced at runtime
// with VITE_MAPTILER_KEY so the real key never lands in the committed JSON.
const MAPTILER_PLACEHOLDER = 'get_your_own_OpIi9ZULNHzrESv6T2vL';

const OSM_RASTER_STYLE: maplibregl.StyleSpecification = {
  version: 8,
  sources: {
    osm: {
      type: 'raster',
      tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
      tileSize: 256,
      attribution: '© OpenStreetMap contributors',
    },
  },
  layers: [
    {
      id: 'osm',
      type: 'raster',
      source: 'osm',
    },
  ],
};

// osm-liberty pulls vector tiles from MapTiler, which requires a per-app API
// key (free tier available at maptiler.com). Inject it into the TileJSON URL
// at runtime; without a key MapTiler answers 403 and the map stays blank, so
// callers fall back to the raster style instead.
function osmLibertyStyleWithKey(key: string): maplibregl.StyleSpecification {
  const json = JSON.stringify(osmLibertyStyle).split(MAPTILER_PLACEHOLDER).join(key);
  return JSON.parse(json) as unknown as maplibregl.StyleSpecification;
}

function getMapStyle(): string | maplibregl.StyleSpecification {
  const requested = config.mapStyleUrl;
  if (requested === OSM_RASTER) return OSM_RASTER_STYLE;
  if (requested === OSM_LIBERTY) {
    if (config.maptilerKey) return osmLibertyStyleWithKey(config.maptilerKey);
    console.error(
      '[map] osm-liberty selected but VITE_MAPTILER_KEY is empty — falling back to osm-raster. ' +
        'Get a free key at https://cloud.maptiler.com and set VITE_MAPTILER_KEY.',
    );
    return OSM_RASTER_STYLE;
  }
  return requested;
}

function markerLabel(index: number): string {
  return String.fromCharCode(65 + index); // A, B, C, D…
}

function markerColor(index: number): string {
  if (index === 0) return '#2ea6ff'; // pickup — blue
  if (index === 1) return '#ff5c5c'; // destination — red
  if (index === 2) return '#ffb800'; // stop — amber
  return '#8a8f98'; // extra stops — gray
}

function createLabeledMarker(
  map: maplibregl.Map,
  point: RoutePoint,
  index: number,
): maplibregl.Marker {
  const el = document.createElement('div');
  el.className = 'map-marker';
  el.style.setProperty('--mk-color', markerColor(index));
  el.textContent = markerLabel(index);
  return new maplibregl.Marker({ element: el })
    .setLngLat([point.lon, point.lat])
    .addTo(map);
}

function lineData(coords: Array<[number, number]>): Feature<LineString> | { type: 'FeatureCollection'; features: [] } {
  if (coords.length > 1) {
    return {
      type: 'Feature',
      properties: {},
      geometry: { type: 'LineString', coordinates: coords },
    };
  }
  return { type: 'FeatureCollection', features: [] };
}

// MapLibre GL map. Renders a marker per route point, the encoded route polyline
// once a quote is available, and a dashed straight-line preview through the
// entered points while the route is still being built. Geocoding/routing come
// from the backend cache proxy — this component only draws.
export function MapView({ center, points, routeGeometry, driver, driverRouteGeometry, followDriver, nearbyDrivers }: MapViewProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const markersRef = useRef<Record<string, maplibregl.Marker>>({});
  const styleReadyRef = useRef(false);
  const trackerRef = useRef<DriverTracker | null>(null);
  const driverMarkerRef = useRef<maplibregl.Marker | null>(null);
  const followRef = useRef(followDriver ?? false);
  const nearbyMarkersRef = useRef<Map<string, maplibregl.Marker>>(new Map());
  const nearbyPrevRef = useRef<Map<string, [number, number]>>(new Map());
  const nearbyDriversRef = useRef<NearbyDriver[]>(nearbyDrivers ?? []);

  const pointsRef = useRef<PointSlot[]>(points ?? []);
  const routeGeomRef = useRef<string | null>(routeGeometry ?? null);
  const centerRef = useRef<LatLon | null | undefined>(center);

  const setLine = useCallback(
    (
      map: maplibregl.Map,
      sourceId: string,
      layerId: string,
      coords: Array<[number, number]>,
      paint: Record<string, unknown>,
    ) => {
      const data = lineData(coords);
      const src = map.getSource(sourceId) as maplibregl.GeoJSONSource | undefined;
      if (src) {
        src.setData(data as GeoJSON.Feature<GeoJSON.Geometry> | GeoJSON.FeatureCollection<GeoJSON.Geometry>);
      } else {
        map.addSource(sourceId, { type: 'geojson', data: data as never });
        map.addLayer({
          id: layerId,
          type: 'line',
          source: sourceId,
          layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: paint as never,
        });
      }
      if (paint['line-dasharray'] !== undefined) {
        map.setPaintProperty(layerId, 'line-dasharray', paint['line-dasharray'] as never);
      } else {
        // Clear a previously-set dash when switching back to a solid line.
        try {
          map.setPaintProperty(layerId, 'line-dasharray', null as never);
        } catch {
          // property may not exist yet on first add — ignore
        }
      }
    },
    [],
  );

  const fitToCoords = useCallback((map: maplibregl.Map, coords: Array<[number, number]>) => {
    if (coords.length < 2) return;
    const bounds = coords.reduce(
      (b, c) => b.extend(c),
      new maplibregl.LngLatBounds(coords[0], coords[0]),
    );
    map.fitBounds(bounds, { padding: 60, maxZoom: 15, duration: 400 });
  }, []);

  const syncMarkers = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    const filled = (pointsRef.current ?? []).filter((p): p is RoutePoint => p !== null);
    const present = new Set(filled.map((_, i) => String(i)));

    // Reconcile point markers. The driver marker is owned by the tracker below.
    for (const key of Object.keys(markersRef.current)) {
      if (!present.has(key)) {
        markersRef.current[key]?.remove();
        delete markersRef.current[key];
      }
    }
    filled.forEach((p, i) => {
      const key = String(i);
      const existing = markersRef.current[key];
      if (existing) {
        existing.setLngLat([p.lon, p.lat]);
      } else {
        markersRef.current[key] = createLabeledMarker(map, p, i);
      }
    });
  }, []);

  // Reconcile nearby free-car markers. Each poll (3s) updates positions; the
  // icon is rotated by bearing from the previous sample so cars appear to face
  // their travel direction. Stale ids (no longer in the response) are removed.
  const syncNearbyMarkers = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    const drivers = nearbyDriversRef.current;
    const present = new Set(drivers.map((d) => d.driverId));

    for (const [id, marker] of nearbyMarkersRef.current) {
      if (!present.has(id)) {
        marker.remove();
        nearbyMarkersRef.current.delete(id);
        nearbyPrevRef.current.delete(id);
      }
    }

    for (const d of drivers) {
      const lngLat: [number, number] = [d.lon, d.lat];
      const prev = nearbyPrevRef.current.get(d.driverId);
      const rotation = prev ? bearingDeg({ lon: prev[0], lat: prev[1] }, { lon: lngLat[0], lat: lngLat[1] }) : 0;
      nearbyPrevRef.current.set(d.driverId, lngLat);

      const existing = nearbyMarkersRef.current.get(d.driverId);
      if (existing) {
        existing.setLngLat(lngLat);
        existing.setRotation(rotation);
      } else {
        const el = document.createElement('div');
        el.className = 'map-marker nearby-car';
        el.innerHTML = NEARBY_CAR_SVG;
        const marker = new maplibregl.Marker({ element: el, rotationAlignment: 'map', rotation })
          .setLngLat(lngLat)
          .addTo(map);
        nearbyMarkersRef.current.set(d.driverId, marker);
      }
    }
  }, []);

  const syncRoute = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    const filled = (pointsRef.current ?? []).filter((p): p is RoutePoint => p !== null);

    const routeCoords = routeGeomRef.current ? decodePolyline(routeGeomRef.current) : [];
    const hasRoute = routeCoords.length > 1;

    setLine(map, ROUTE_SOURCE, ROUTE_LAYER, routeCoords, {
      'line-color': '#2ea6ff',
      'line-width': 5,
    });

    // While the user is still building the route (no quote yet), show a dashed
    // straight preview through the entered points so the map is never empty.
    const previewCoords = !hasRoute && filled.length >= 2
      ? filled.map((p) => [p.lon, p.lat] as [number, number])
      : [];
    setLine(map, PREVIEW_SOURCE, PREVIEW_LAYER, previewCoords, {
      'line-color': '#2ea6ff',
      'line-width': 3,
      'line-dasharray': [1, 2],
    });

    if (hasRoute) {
      fitToCoords(map, routeCoords);
    } else if (filled.length >= 2) {
      fitToCoords(map, filled.map((p) => [p.lon, p.lat] as [number, number]));
    } else if (filled.length === 1) {
      map.easeTo({ center: [filled[0].lon, filled[0].lat], zoom: 14, duration: 400 });
    } else if (centerRef.current) {
      map.easeTo({ center: [centerRef.current.lon, centerRef.current.lat], zoom: 12, duration: 400 });
    }
  }, [setLine, fitToCoords]);

  // Init map once.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: getMapStyle(),
      center: [config.mapDefaultLon, config.mapDefaultLat],
      zoom: 13,
      attributionControl: false,
    });
    mapRef.current = map;

    const onReady = () => {
      styleReadyRef.current = true;
      syncMarkers();
      syncRoute();
      syncNearbyMarkers();
    };
    if (map.isStyleLoaded()) onReady();
    else map.once('load', onReady);

    return () => {
      trackerRef.current?.stop();
      trackerRef.current = null;
      driverMarkerRef.current?.remove();
      driverMarkerRef.current = null;
      for (const marker of nearbyMarkersRef.current.values()) marker.remove();
      nearbyMarkersRef.current.clear();
      nearbyPrevRef.current.clear();
      map.remove();
      mapRef.current = null;
      markersRef.current = {};
      styleReadyRef.current = false;
    };
  }, [syncMarkers, syncRoute, syncNearbyMarkers]);

  // Create the driver tracker once the map exists. The car marker is created
  // lazily on the first tick and updated every 100ms (interpolated along the
  // driver route polyline + rotated by bearing). Follow-camera keeps the car
  // centered when followRef is set.
  useEffect(() => {
    if (!mapRef.current) return;
    const tracker = new DriverTracker();
    tracker.onTick = ({ lat, lon, bearing }) => {
      const map = mapRef.current;
      if (!map) return;
      let m = driverMarkerRef.current;
      if (!m) {
        const el = document.createElement('div');
        el.className = 'map-marker car';
        el.innerHTML = CAR_SVG;
        m = new maplibregl.Marker({ element: el, rotationAlignment: 'map', rotation: 0 })
          .setLngLat([lon, lat])
          .addTo(map);
        driverMarkerRef.current = m;
      }
      m.setLngLat([lon, lat]);
      m.setRotation(bearing);
      if (followRef.current) {
        map.easeTo({
          center: [lon, lat],
          zoom: Math.max(map.getZoom(), 15),
          duration: 200,
        });
      }
    };
    trackerRef.current = tracker;
    tracker.start();
    return () => {
      tracker.stop();
      driverMarkerRef.current?.remove();
      driverMarkerRef.current = null;
    };
  }, []);

  // Feed raw driver positions to the tracker. When the driver clears, drop the
  // marker — tracking has stopped.
  useEffect(() => {
    const tracker = trackerRef.current;
    if (driver) {
      tracker?.setRaw(driver);
    } else {
      driverMarkerRef.current?.remove();
      driverMarkerRef.current = null;
    }
  }, [driver]);

  // Driver route polyline (driver->pickup, or the order route during a trip):
  // draw it as a separate line and feed the points to the tracker so the car
  // animates along the actual road. Fit once when it first appears (unless the
  // user is following the car).
  useEffect(() => {
    const map = mapRef.current;
    const tracker = trackerRef.current;
    if (!map || !tracker || !styleReadyRef.current) return;
    const lonLat = driverRouteGeometry ? decodePolyline(driverRouteGeometry) : [];
    // The driver route doubles as the trip route (A->B) during TRIP_IN_PROGRESS;
    // in that case the order route line already shows it, so skip the amber
    // overlay to avoid two coincident lines. The tracker still gets the points.
    const isDuplicate = !!driverRouteGeometry && driverRouteGeometry === routeGeometry;
    setLine(
      map,
      DRIVER_ROUTE_SOURCE,
      DRIVER_ROUTE_LAYER,
      isDuplicate ? [] : lonLat,
      { 'line-color': '#f59e0b', 'line-width': 4 },
    );
    tracker.setRoute(lonLat.map((c) => ({ lon: c[0], lat: c[1] })));
    if (lonLat.length > 1 && !followRef.current) {
      fitToCoords(map, lonLat);
    }
  }, [driverRouteGeometry, routeGeometry, setLine, fitToCoords]);

  // Sync point markers when points change.
  useEffect(() => {
    pointsRef.current = points ?? [];
    if (styleReadyRef.current) syncMarkers();
  }, [points, syncMarkers]);

  // Keep a ref of the follow toggle so the tracker tick closure reads the
  // latest value without restarting the animation.
  useEffect(() => {
    followRef.current = followDriver ?? false;
  }, [followDriver]);

  // Sync nearby free-car markers when the ether list updates.
  useEffect(() => {
    nearbyDriversRef.current = nearbyDrivers ?? [];
    if (styleReadyRef.current) syncNearbyMarkers();
  }, [nearbyDrivers, syncNearbyMarkers]);

  // Sync route line + camera when the route or points change.
  useEffect(() => {
    pointsRef.current = points ?? [];
    routeGeomRef.current = routeGeometry ?? null;
    centerRef.current = center;
    if (styleReadyRef.current) syncRoute();
  }, [points, routeGeometry, center, syncRoute]);

  return <div ref={containerRef} style={{ position: 'absolute', inset: 0 }} />;
}
